import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Search, Trash2, Image as ImageIcon, Music, Video, FileText,
  Loader2, ExternalLink, X, AlertTriangle, RefreshCw,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authFetch = (path, opts = {}) => {
  const token = localStorage.getItem('rbr_token');
  return fetch(`${API_URL}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
};

const kb = (n) => {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const KIND_ICON = {
  audio: Music, video: Video, image: ImageIcon,
  image_cover: ImageIcon, image_content: ImageIcon,
  captions: FileText,
};

// -------------------- Preview panel (audio/video/image) --------------------

const MediaPreview = ({ asset }) => {
  if (!asset) return null;
  const src = `${API_URL}${asset.url}`;
  if (asset.kind === 'audio') {
    return <audio controls src={src} preload="none" className="w-full mt-2" data-testid="media-preview-audio" />;
  }
  if (asset.kind === 'video') {
    return <video controls src={src} preload="metadata" className="w-full mt-2 rounded-lg max-h-72 bg-black" data-testid="media-preview-video" />;
  }
  if ((asset.kind || '').startsWith('image')) {
    return <img src={src} alt={asset.originalFilename} className="w-full mt-2 rounded-lg object-cover max-h-72" data-testid="media-preview-image" />;
  }
  return <p className="mt-2 text-[12px] text-stone-500">No preview available for {asset.kind}.</p>;
};

// -------------------- Detail modal --------------------

const AssetDetailModal = ({ open, onClose, assetId, onDeleted }) => {
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!assetId) return;
    setLoading(true);
    try {
      const r = await authFetch(`/api/admin/media/${assetId}`);
      if (r.ok) setAsset(await r.json());
    } finally { setLoading(false); }
  }, [assetId]);

  useEffect(() => { if (open) { setConfirming(false); load(); } }, [open, load]);

  if (!open) return null;

  const doDelete = async () => {
    if (!confirming) { setConfirming(true); return; }
    setBusy(true);
    try {
      const r = await authFetch(`/api/admin/media/${assetId}`, { method: 'DELETE' });
      const data = await r.json();
      if (!r.ok) {
        // Dependency-blocked delete surfaces a friendly message.
        if (r.status === 409 && data?.detail?.dependencies) {
          toast.error(data.detail.message || 'Asset is linked to content and cannot be deleted');
        } else {
          toast.error(typeof data.detail === 'string' ? data.detail : 'Delete failed');
        }
        setConfirming(false);
        return;
      }
      toast.success('Media asset soft-deleted');
      onDeleted();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" data-testid="media-detail-modal" onClick={onClose}>
      <div className="bg-[#FFFCF6] w-full max-w-2xl rounded-2xl border border-stone-200 shadow-lg p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-xl text-stone-800 truncate pr-4" style={{ fontFamily: "'DM Serif Display', serif" }}>
            {asset?.originalFilename || 'Media asset'}
          </h3>
          <button onClick={onClose} className="p-1 text-stone-400 hover:text-stone-700"><X className="w-4 h-4" /></button>
        </div>

        {loading || !asset ? (
          <p className="text-center py-8 text-stone-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</p>
        ) : (
          <>
            <MediaPreview asset={asset} />

            <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
              <p className="text-stone-500">Kind</p><p className="font-semibold text-stone-800">{asset.kind}</p>
              <p className="text-stone-500">Content type</p><p className="font-semibold text-stone-800">{asset.contentType || '—'}</p>
              <p className="text-stone-500">Size</p><p className="font-semibold text-stone-800">{kb(asset.size)}</p>
              <p className="text-stone-500">Uploaded</p><p className="font-semibold text-stone-800">{asset.createdAt ? new Date(asset.createdAt).toLocaleString() : '—'}</p>
              <p className="text-stone-500">Asset ID</p><p className="font-mono text-[11px] text-stone-700 break-all">{asset.id}</p>
              <p className="text-stone-500">SHA-256</p><p className="font-mono text-[11px] text-stone-700 break-all" data-testid="media-detail-sha">{asset.sha256 || <span className="italic text-stone-400">not stored (pre-Phase-C upload)</span>}</p>
            </div>

            <div className="mt-4 pt-4 border-t border-stone-200">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-2" data-testid="media-detail-linked-count">
                Linked content ({asset.linkedContentCount})
              </p>
              {(asset.linkedContent || []).length === 0 ? (
                <p className="text-[12px] text-stone-400 italic">Orphan — this asset is not used by any content record.</p>
              ) : (
                <ul className="space-y-1.5">
                  {asset.linkedContent.map((c) => (
                    <li key={c.id} data-testid={`media-detail-linked-${c.id}`} className="flex items-center gap-2 text-[12px]">
                      <span className="text-[10px] uppercase tracking-wider text-[#C79A4A] font-bold w-24 truncate">{c.type}</span>
                      <span className="text-stone-800 flex-1 truncate">{c.title}</span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${
                        c.status === 'published' ? 'bg-emerald-50 text-emerald-700' :
                        c.status === 'scheduled' ? 'bg-sky-50 text-sky-700' :
                        c.status === 'archived'  ? 'bg-stone-100 text-stone-500' :
                                                    'bg-amber-50 text-amber-700'
                      }`}>{c.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex gap-2 mt-5 pt-4 border-t border-stone-200 justify-end">
              <a href={`${API_URL}${asset.url}`} target="_blank" rel="noreferrer"
                 className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm border border-stone-200 hover:bg-white">
                <ExternalLink className="w-4 h-4" /> Open in new tab
              </a>
              <button
                onClick={doDelete}
                disabled={busy}
                data-testid="media-detail-delete"
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm disabled:opacity-60 ${
                  confirming ? 'bg-rose-600 text-white hover:bg-rose-700' : 'border border-rose-200 text-rose-600 hover:bg-rose-50'
                }`}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {confirming ? 'Tap again to confirm' : (asset.linkedContentCount > 0 ? 'Delete (blocked while linked)' : 'Delete asset')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// -------------------- Root Media Library tab --------------------

const MediaLibraryTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ q: '', kind: '', linked: '', sort: 'createdAt_desc' });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [modalAsset, setModalAsset] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      params.set('page', String(page));
      params.set('pageSize', '24');
      const r = await authFetch(`/api/admin/media?${params.toString()}`);
      const d = await r.json();
      if (r.ok) {
        setItems(d.items || []);
        setTotal(d.total || 0);
        setTotalPages(d.totalPages || 1);
      }
    } finally { setLoading(false); }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };

  return (
    <div data-testid="admin-media-tab" className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 absolute top-2.5 left-3 text-stone-400" />
          <input
            value={filters.q}
            onChange={(e) => setFilter('q', e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
            placeholder="Search filenames…"
            className="pl-9 w-full px-3 py-2.5 rounded-xl bg-white border border-stone-200 focus:border-[#C79A4A] focus:outline-none text-sm"
            data-testid="media-search"
          />
        </div>
        <select value={filters.kind} onChange={(e) => setFilter('kind', e.target.value)} className="px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm" data-testid="media-kind-filter">
          <option value="">All kinds</option>
          <option value="audio">Audio</option>
          <option value="video">Video</option>
          <option value="image_content">Image (content)</option>
          <option value="image_cover">Image (cover)</option>
          <option value="captions">Captions</option>
        </select>
        <select value={filters.linked} onChange={(e) => setFilter('linked', e.target.value)} className="px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm" data-testid="media-linked-filter">
          <option value="">All (linked + orphan)</option>
          <option value="only">Linked only</option>
          <option value="orphan">Orphans only</option>
        </select>
        <select value={filters.sort} onChange={(e) => setFilter('sort', e.target.value)} className="px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm" data-testid="media-sort">
          <option value="createdAt_desc">Newest</option>
          <option value="createdAt_asc">Oldest</option>
          <option value="filename_asc">Filename A→Z</option>
          <option value="filename_desc">Filename Z→A</option>
          <option value="size_desc">Size ↓</option>
          <option value="size_asc">Size ↑</option>
        </select>
        <button onClick={load} data-testid="media-refresh" className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] border border-stone-200 hover:bg-white">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
        <span className="ml-auto text-[12px] text-stone-500" data-testid="media-total">{total} assets</span>
      </div>

      {loading ? (
        <p className="text-center text-stone-500 py-8"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</p>
      ) : items.length === 0 ? (
        <div className="text-center py-10 bg-[#FFFCF6] rounded-2xl border border-stone-200">
          <p className="text-stone-800" style={{ fontFamily: "'DM Serif Display', serif" }}>No media matches these filters.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map((a) => {
            const Icon = KIND_ICON[a.kind] || FileText;
            return (
              <button
                key={a.id}
                onClick={() => setModalAsset(a.id)}
                data-testid={`media-card-${a.id}`}
                className="text-left bg-[#FFFCF6] rounded-xl border border-stone-200 hover:border-[#C79A4A] p-3 transition-colors"
              >
                <div className="aspect-video rounded-lg bg-[#EFE7D4] flex items-center justify-center overflow-hidden mb-2">
                  {(a.kind || '').startsWith('image') ? (
                    <img src={`${API_URL}${a.url}`} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Icon className="w-10 h-10 text-[#C79A4A] opacity-60" />
                  )}
                </div>
                <p className="text-[12px] font-semibold text-stone-800 truncate">{a.originalFilename || 'Unnamed'}</p>
                <div className="flex items-center gap-2 mt-1 text-[10px] text-stone-500">
                  <span className="uppercase tracking-wider font-bold text-[#C79A4A]">{a.kind}</span>
                  <span>·</span>
                  <span>{kb(a.size)}</span>
                  {a.linkedContentCount > 0 ? (
                    <span className="ml-auto text-emerald-700 font-semibold">{a.linkedContentCount} linked</span>
                  ) : (
                    <span className="ml-auto text-amber-700 font-semibold inline-flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" /> orphan</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between mt-2 text-[12px] text-stone-500">
        <span>Page {page} of {totalPages}</span>
        <div className="flex gap-2">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} data-testid="media-page-prev" className="px-3 py-1 rounded-lg border border-stone-200 disabled:opacity-40 hover:bg-white">Prev</button>
          <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages} data-testid="media-page-next" className="px-3 py-1 rounded-lg border border-stone-200 disabled:opacity-40 hover:bg-white">Next</button>
        </div>
      </div>

      <AssetDetailModal
        open={!!modalAsset}
        onClose={() => setModalAsset(null)}
        assetId={modalAsset}
        onDeleted={() => { setModalAsset(null); load(); }}
      />
    </div>
  );
};

export default MediaLibraryTab;
